import {useEffect, useMemo, useRef, useState} from 'react';
import {
    HubConnectionBuilder,
    HttpTransportType,
    HubConnection,
    HubConnectionState
} from '@microsoft/signalr';
import {useAuthStateManager} from '../hooks/useAuthStateManager.tsx';
import baseUri from '../api/baseUri.ts';
import {isRenderMode} from '../lib/tabView.ts';
import hubReconnectPolicy from '../lib/hubReconnectPolicy.ts';

enum ExceptionReason {
    General = 'General exception',
    Start = 'Could not start connection',
    Invoke = 'Could not invoke hub method'
}

const logException = (
    exceptionReason: ExceptionReason,
    exception: unknown,
    groupName: string
) => {
    console.error(`${groupName} ${exceptionReason}`);
    console.error(exception);
};

export default function useProjectDataHub(
    groupName: string
): [HubConnection, boolean, () => void] {
    const {getAccessToken, authData} = useAuthStateManager();
    const [hubState, setHubState] = useState(HubConnectionState.Connecting);
    const [reinitialize, setReinitialize] = useState(false);

    // The connection outlives a change of group, and its reconnect handler is registered once, so it reads the
    // group from here.
    const groupNameRef = useRef(groupName);
    groupNameRef.current = groupName;

    // A new access token comes every hour, and with it a new getAccessToken. The connection asks for the token each
    // time it connects, so it reads the current function from here rather than being rebuilt for each one: a rebuilt
    // connection meant an hourly disconnect, and anything still holding the old one sent edits nowhere.
    const getAccessTokenRef = useRef(getAccessToken);
    getAccessTokenRef.current = getAccessToken;

    const hubConnection = useMemo(() => {
        const hubConnector = new HubConnectionBuilder()
            // The bearer token (accessTokenFactory) identifies the person.
            .withUrl(`${baseUri}/projectDataHub`, {
                transport: HttpTransportType.WebSockets,
                accessTokenFactory: () => getAccessTokenRef.current()
            })
            .withAutomaticReconnect(hubReconnectPolicy)
            .build();

        // The server ignores edits from a connection that hasn't joined, so the rejoin is the new connection's first
        // call; the server handles one connection's calls in order, so edits sent after it land in the group. While
        // reconnecting, useProjectData sends nothing (it only sends while Connected).
        hubConnector.onreconnecting(() => {
            setHubState(HubConnectionState.Reconnecting);
        });

        hubConnector.onreconnected(() => {
            hubConnector
                .invoke('join', groupNameRef.current)
                .then(() => setHubState(HubConnectionState.Connected))
                .catch(ex => {
                    logException(
                        ExceptionReason.Invoke,
                        ex,
                        groupNameRef.current
                    );
                    // Stopping shows the reconnection prompt rather than leaving a connection that drops every edit.
                    hubConnector.stop();
                });
        });

        hubConnector.onclose(error => {
            setHubState(HubConnectionState.Disconnected);
            if (error) {
                console.error(error);
            }
        });

        return hubConnector;
        // A different person signed in, or the reconnect button: a new connection. Not a new token for the same one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData.id, reinitialize]);

    useEffect(() => {
        // The render page takes one picture of the tab as it is; it has no use for live changes, and its render
        // ticket isn't accepted by the hub.
        if (isRenderMode()) {
            return;
        }

        if (
            hubConnection.state === HubConnectionState.Connected ||
            hubConnection.state === HubConnectionState.Connecting
        ) {
            return; // Skip if already connected or connecting
        }

        setHubState(HubConnectionState.Connecting);

        let attempts = 0;
        let tryConnection: ReturnType<typeof setTimeout>;
        let isMounted = true;

        const tryCreateHubConnection = async () => {
            let exceptionReason = ExceptionReason.General;

            try {
                if (
                    hubConnection.state !== HubConnectionState.Connecting &&
                    hubConnection.state !== HubConnectionState.Connected
                ) {
                    await hubConnection.start().catch(ex => {
                        exceptionReason = ExceptionReason.Start;
                        throw ex;
                    });

                    await hubConnection.invoke('join', groupName).catch(ex => {
                        exceptionReason = ExceptionReason.Invoke;
                        throw ex;
                    });

                    if (isMounted) {
                        setHubState(HubConnectionState.Connected);
                    }
                }
            } catch (ex) {
                if (attempts < 4) {
                    tryConnection = setTimeout(
                        () => tryCreateHubConnection(),
                        1000 * Math.pow(2, attempts)
                    );
                    attempts++;
                    return;
                }

                logException(exceptionReason, ex, groupName);
                if (isMounted) {
                    setHubState(HubConnectionState.Disconnected);
                }
            }
        };

        tryCreateHubConnection();

        return () => {
            isMounted = false;
            if (hubConnection.state !== HubConnectionState.Disconnected) {
                hubConnection.stop().then(() => {
                    if (isMounted) {
                        setHubState(HubConnectionState.Disconnected);
                        console.log('Connection stopped');
                    }
                });
            }
            clearTimeout(tryConnection);
        };
    }, [groupName, hubConnection, reinitialize]);

    return [
        hubConnection,
        hubState === HubConnectionState.Disconnected,
        () => setReinitialize(!reinitialize)
    ];
}

export const ProjectDataHubMessages = {
    UpdateProjectDataOnClient: 'updateProjectDataOnClient',
    UpdateProjectDataOnServer: 'updateProjectDataOnServer',
    UpdatePlayerOnServer: 'updatePlayerOnServer',
    UpdatePlayerOnClient: 'updatePlayerOnClient',
    UpdateReactionOnServer: 'updateReactionOnServer',
    UpdateReactionOnClient: 'updateReactionOnClient',
    // A scene, vibe code or blocks tab's document was saved (SceneDocumentUpdate).
    SceneDocumentUpdatedOnClient: 'sceneDocumentUpdatedOnClient'
};

// The payload of SceneDocumentUpdatedOnClient.
export interface SceneDocumentUpdate {
    projectId: string;
    dataIndex: number;
    syncToken: string;
    userId: string | null;
    // The AI app that made the change, when one did (for example "Claude").
    actor: string | null;
    atUtc: string;
}

export const useHubMessageListener = (
    hubConnection: HubConnection,
    message: string,
    handler: (...args: unknown[]) => void = () => {
        return;
    },
    dependencies: unknown[] = []
) => {
    useEffect(() => {
        hubConnection.on(message, (...args: unknown[]) => {
            try {
                handler(...args);
            } catch (ex) {
                // Not notifying the user here as it may not actually be an issue.
                console.error(ex);
            }
        });
        return () => {
            hubConnection.off(message);
        };
    }, dependencies);
};
